/**
 * CustomerIllustrationsTab.tsx
 * Component độc lập hiển thị danh sách Tài liệu minh họa của khách hàng.
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Icon } from '../Icon';
import { colors, spacing, fontWeights, borderRadius } from '../../theme';
import type { CustomerIllustration } from '../../data/customers';

export interface CustomerIllustrationsTabProps {
  illustrations: CustomerIllustration[];
  onView?: (illustration: CustomerIllustration) => void;
  onEdit?: (illustration: CustomerIllustration) => void;
  onDownloadPDF?: (illustration: CustomerIllustration) => void;
}

const STAGE_META: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'Mới tạo', color: '#2563EB', bg: '#EFF6FF' },
  negotiating: { label: 'Đang tư vấn', color: '#D97706', bg: '#FFFBEB' },
  closed: { label: 'Đã chốt', color: '#00A651', bg: '#F0FDF4' },
};

export const CustomerIllustrationsTab: React.FC<CustomerIllustrationsTabProps> = ({
  illustrations,
  onView,
  onEdit,
  onDownloadPDF,
}) => {
  if (!illustrations || illustrations.length === 0) {
    return (
      <View style={styles.emptyBox}>
        <Icon name="description" size={32} color={colors.outlineVariant} />
        <Text style={styles.emptyText}>Chưa có tài liệu minh họa cho khách hàng này</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {illustrations.map((i) => {
        const stageInfo = STAGE_META[i.stage] || STAGE_META.new;
        return (
          <View key={i.id} style={styles.illustCard}>
            <View style={styles.illustCardHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.illustTitle}>{i.proposalName}</Text>
                <Text style={styles.illustId}>Mã TLMH: {i.id}</Text>
              </View>
              <View style={[styles.stageBadge, { backgroundColor: stageInfo.bg }]}>
                <Text style={[styles.stageBadgeText, { color: stageInfo.color }]}>
                  {stageInfo.label}
                </Text>
              </View>
            </View>

            <View style={styles.illustStatsRow}>
              <View style={styles.illustStatItem}>
                <Text style={styles.illustStatVal}>{i.premium}k</Text>
                <Text style={styles.illustStatLbl}>Phí BH/Năm</Text>
              </View>
              <View style={styles.illustStatItem}>
                <Text style={styles.illustStatVal}>{i.sumAssured}</Text>
                <Text style={styles.illustStatLbl}>STBH (VNĐ)</Text>
              </View>
              <View style={styles.illustStatItem}>
                <Text style={styles.illustStatVal}>{i.term}</Text>
                <Text style={styles.illustStatLbl}>Thời hạn</Text>
              </View>
            </View>

            <View style={styles.illustCardActions}>
              {onView && (
                <TouchableOpacity
                  style={[styles.miniActionBtn, styles.miniViewBtn]}
                  onPress={() => onView(i)}
                >
                  <Icon name="visibility" size={14} color={colors.primary} />
                  <Text style={[styles.miniActionText, { color: colors.primary }]}>Xem</Text>
                </TouchableOpacity>
              )}
              {onEdit && (
                <TouchableOpacity
                  style={[styles.miniActionBtn, styles.miniEditBtn]}
                  onPress={() => onEdit(i)}
                >
                  <Icon name="edit" size={14} color="#D97706" />
                  <Text style={[styles.miniActionText, { color: '#D97706' }]}>Sửa</Text>
                </TouchableOpacity>
              )}
              {onDownloadPDF && (
                <TouchableOpacity
                  style={[styles.miniActionBtn, styles.miniPdfBtn]}
                  onPress={() => onDownloadPDF(i)}
                >
                  <Icon name="picture_as_pdf" size={14} color="#EF4444" />
                  <Text style={[styles.miniActionText, { color: '#EF4444' }]}>PDF</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing[3],
  },
  emptyBox: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing[8],
    gap: spacing[2],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  illustCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.DEFAULT,
    padding: 14,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[2.5],
  },
  illustCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  illustTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  illustId: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  stageBadge: {
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  stageBadgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },
  illustStatsRow: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    padding: spacing[2.5],
    borderRadius: borderRadius.DEFAULT,
    justifyContent: 'space-around',
  },
  illustStatItem: {
    alignItems: 'center',
  },
  illustStatVal: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  illustStatLbl: {
    fontSize: 10,
    color: colors.onSurfaceVariant,
  },
  illustCardActions: {
    flexDirection: 'row',
    gap: spacing[2],
    justifyContent: 'flex-end',
  },
  miniActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing[2.5],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.full,
    borderWidth: 1,
  },
  miniViewBtn: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6',
  },
  miniEditBtn: {
    backgroundColor: '#FFF7ED',
    borderColor: '#F59E0B',
  },
  miniPdfBtn: {
    backgroundColor: '#FEF2F2',
    borderColor: '#EF4444',
  },
  miniActionText: {
    fontSize: 11,
    fontWeight: fontWeights.semibold,
  },
});
